import {
  changeMFAConfig,
  loginProvider,
  registerManualProvider,
  resendMFACode,
  resetProviderPassword,
  sendProviderPasswordCode,
  updateProviderPassword,
  validateMFACode,
  validateProviderPasswordCode,
} from "@/app/api/auth";
import {
  LoginDTO,
  RegisterManualDTO,
  ResetPasswordDTO,
  UpdatePasswordDTO,
  ValidateCodeDTO,
} from "@/app/api/auth/types";
import { ErrorResponse } from "@/app/types/error";
import { ELocalStorageKeys } from "@/app/types/localStorage";
import {
  getItemLocaleStorage,
  removeItemLocalStorage,
  setItemSessionStorage,
} from "@/lib/utils";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

interface AuthState {
  auth: { email: string; password: string };
  validate: ValidateCodeDTO;
  requiresMFA: boolean;
  resetPassword: ResetPasswordDTO;
  updatePassword: UpdatePasswordDTO;
  manualRegisterData: RegisterManualDTO;
  accessToken: string;
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
}

const initialState: AuthState = {
  auth: { email: "", password: "" },
  validate: {} as ValidateCodeDTO,
  requiresMFA: false,
  resetPassword: {} as ResetPasswordDTO,
  updatePassword: {} as UpdatePasswordDTO,
  manualRegisterData: {} as RegisterManualDTO,
  accessToken: "",
  status: "idle",
  error: null,
};

export const login = createAsyncThunk(
  "auth/provider/login",
  async (data: LoginDTO, { rejectWithValue }) => {
    try {
      const response = await loginProvider(data);

      if (
        response &&
        typeof response === "object" &&
        "status" in response &&
        response.status === "fail"
      ) {
        return rejectWithValue((response as ErrorResponse).message);
      }

      if (response.requiresMfa) {
        return { type: "MFA_REQUIRED", requiresMfa: true };
      }

      if (getItemLocaleStorage(ELocalStorageKeys.TOKEN)) {
        removeItemLocalStorage(ELocalStorageKeys.TOKEN);
      }

      if (response.token.accessToken) {
        setItemSessionStorage(
          ELocalStorageKeys.TOKEN,
          response.token.accessToken,
        );

        return {
          type: "AUTHENTICATED",
          accessToken: response.token.accessToken,
        };
      }

      return response;
    } catch (error) {
      if (error instanceof Error && error.message) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("An unknown error occurred");
    }
  },
);

export const manualRegister = createAsyncThunk(
  "auth/provider/manual/register",
  async (data: RegisterManualDTO, { rejectWithValue }) => {
    try {
      const response = await registerManualProvider(data);

      if (
        response &&
        typeof response === "object" &&
        "status" in response &&
        response.status === "fail"
      ) {
        return rejectWithValue((response as ErrorResponse).message);
      }

      setItemSessionStorage(ELocalStorageKeys.TOKEN, response.data.accessToken);

      return response as { data: { accessToken: string } };
    } catch (error) {
      if (error instanceof Error && error.message) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("An unknown error occurred");
    }
  },
);

export const sendPasswordCode = createAsyncThunk(
  "auth/provider/sendPasswordCode",
  async (email: string, { rejectWithValue }) => {
    try {
      const response = await sendProviderPasswordCode(email);

      if (
        response &&
        typeof response === "object" &&
        "status" in response &&
        response.status === "fail"
      ) {
        return rejectWithValue((response as ErrorResponse).message);
      }

      return response;
    } catch (error) {
      if (error instanceof Error && error.message) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("An unknown error occurred");
    }
  },
);

export const validatePasswordCode = createAsyncThunk(
  "auth/provider/validatePasswordCode",
  async (data: ValidateCodeDTO, { rejectWithValue }) => {
    try {
      const response = await validateProviderPasswordCode(data);

      if (
        response &&
        typeof response === "object" &&
        "status" in response &&
        response.status === "fail"
      ) {
        return rejectWithValue((response as ErrorResponse).message);
      }

      return response;
    } catch (error) {
      if (error instanceof Error && error.message) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("An unknown error occurred");
    }
  },
);

export const resetPassword = createAsyncThunk(
  "auth/provider/resetPassword",
  async (data: ResetPasswordDTO, { rejectWithValue }) => {
    try {
      const response = await resetProviderPassword(data);

      if (
        response &&
        typeof response === "object" &&
        "status" in response &&
        response.status === "fail"
      ) {
        return rejectWithValue((response as ErrorResponse).message);
      }

      return response;
    } catch (error) {
      if (error instanceof Error && error.message) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("An unknown error occurred");
    }
  },
);

export const updatePassword = createAsyncThunk(
  "auth/provider/updatePassword",
  async (data: UpdatePasswordDTO, { rejectWithValue }) => {
    try {
      const response = await updateProviderPassword(data);

      if (
        response &&
        typeof response === "object" &&
        "status" in response &&
        response.status === "fail"
      ) {
        return rejectWithValue((response as ErrorResponse).message);
      }

      return response;
    } catch (error) {
      if (error instanceof Error && error.message) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("An unknown error occurred");
    }
  },
);

export const verifyMfaCode = createAsyncThunk(
  "auth/provider/mfa/vrify",
  async (data: ValidateCodeDTO, { rejectWithValue }) => {
    try {
      const response = await validateMFACode(data);

      if (
        response &&
        typeof response === "object" &&
        "status" in response &&
        response.status === "fail"
      ) {
        return rejectWithValue((response as ErrorResponse).message);
      }

      const accessToken = response.data?.token?.accessToken;

      if (getItemLocaleStorage(ELocalStorageKeys.TOKEN)) {
        removeItemLocalStorage(ELocalStorageKeys.TOKEN);
      }

      if (accessToken) {
        setItemSessionStorage(ELocalStorageKeys.TOKEN, accessToken);
      }

      return response;
    } catch (error) {
      if (error instanceof Error && error.message) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("An unknown error occurred");
    }
  },
);

export const resendMFAEmail = createAsyncThunk(
  "/auth/provider/mfa/send-code",
  async (email: string, { rejectWithValue }) => {
    try {
      const response = await resendMFACode(email);

      if (
        response &&
        typeof response === "object" &&
        "status" in response &&
        response.status === "fail"
      ) {
        return rejectWithValue((response as ErrorResponse).message);
      }

      return response;
    } catch (error) {
      if (error instanceof Error && error.message) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("An unknown error occurred");
    }
  },
);

export const toggleMFAConfig = createAsyncThunk(
  "/auth/provider/mfa/toggle",
  async (status: boolean, { rejectWithValue }) => {
    try {
      const response = await changeMFAConfig(status);

      if (
        response &&
        typeof response === "object" &&
        "status" in response &&
        response.status === "fail"
      ) {
        return rejectWithValue((response as ErrorResponse).message);
      }

      return response;
    } catch (error) {
      if (error instanceof Error && error.message) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("An unknown error occurred");
    }
  },
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      state.status = "idle";
      state.accessToken = "";
    },
    updateAuthState: (state, action) => {
      state.auth = { ...state.auth, ...action.payload };
    },
    updateValidateState: (state, action) => {
      state.validate = { ...state.validate, ...action.payload };
    },
    updateResetPasswordState: (state, action) => {
      state.resetPassword = { ...state.resetPassword, ...action.payload };
    },
    updateUpdatePasswordState: (state, action) => {
      state.updatePassword = { ...state.updatePassword, ...action.payload };
    },
    updateManualRegisterState: (state, action) => {
      state.manualRegisterData = {
        ...state.manualRegisterData,
        ...action.payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.error = null;
        state.auth.email = action.meta.arg.email;
        state.auth.password = action.meta.arg.password;

        if (
          "type" in action.payload &&
          action.payload.type === "AUTHENTICATED" &&
          action.payload.accessToken
        ) {
          state.accessToken = action.payload.accessToken;
          state.requiresMFA = false;
        }

        if (
          "type" in action.payload &&
          action.payload.type === "MFA_REQUIRED"
        ) {
          state.requiresMFA = true;
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || null;
      })
      .addCase(manualRegister.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(manualRegister.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.error = null;
        state.accessToken = action.payload.data.accessToken;
      })
      .addCase(manualRegister.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || null;
      })
      .addCase(sendPasswordCode.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(sendPasswordCode.fulfilled, (state) => {
        state.status = "succeeded";
        state.error = null;
      })
      .addCase(sendPasswordCode.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      })

      .addCase(validatePasswordCode.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(validatePasswordCode.fulfilled, (state) => {
        state.status = "succeeded";
        state.error = null;
      })
      .addCase(validatePasswordCode.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      })

      .addCase(resetPassword.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.status = "succeeded";
        state.error = null;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      })

      .addCase(updatePassword.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(updatePassword.fulfilled, (state) => {
        state.status = "succeeded";
        state.error = null;
      })
      .addCase(updatePassword.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      })
      .addCase(verifyMfaCode.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(verifyMfaCode.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      })
      .addCase(verifyMfaCode.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.error = null;
        state.accessToken = action.payload.data.token.accessToken;
      })
      .addCase(resendMFAEmail.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(resendMFAEmail.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      })
      .addCase(resendMFAEmail.fulfilled, (state) => {
        state.status = "succeeded";
        state.error = null;
      })
      .addCase(toggleMFAConfig.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(toggleMFAConfig.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      })
      .addCase(toggleMFAConfig.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.error = null;
        state.requiresMFA = action.payload.data.MfaEnabled;
      });
  },
});

export const {
  logout,
  updateAuthState,
  updateManualRegisterState,
  updateResetPasswordState,
  updateUpdatePasswordState,
  updateValidateState,
} = authSlice.actions;

export const authReducer = authSlice.reducer;
